/**
 * Access Points view — Phase 2.3.d.
 *
 * Owns the Access Points tab: list of APs, search/scan, AP add/edit
 * dialog, AP enable/disable toggle, and the "assign distributor" dialog
 * that links each AP to a distributor account.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog`, `hasRole`, `filterTable` — generic.
 *   - `getDistributorCache`, `ensureDistributorCache` — admin.js owns the
 *     distributor-shim that proxies to `adminViews.distributors`.
 *   - `getAddProductBtn`, `getScanApButton`, `getAccessPointsTbody` —
 *     admin.js retains the topbar buttons; the table tbody is shared
 *     with the AP rendering here.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports. Distributor cache reaches across to the distributors view
 * lazily via `window.adminViews.distributors.{getCache, ensureCache}` —
 * 3.2.d will replace this with a real cross-view import.
 */

import { escapeHtml, filterTable, formatDateTimeFull } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // Cross-view distributor-cache lookup (lazy — distributors view may not
  // have mounted at the time access-points is parsed).
  const getDistributorCache = () => {
    const d = window.adminViews && window.adminViews.distributors;
    return d ? d.getCache() : { byId: new Map(), items: [] };
  };
  const ensureDistributorCache = () => {
    const d = window.adminViews && window.adminViews.distributors;
    return d ? d.ensureCache() : Promise.resolve({ byId: new Map(), items: [] });
  };
  // DOM lookups for buttons admin.js used to provide via configure().
  const getAddProductBtn = () => document.getElementById('addProductBtn');
  const getScanApButton = () => document.getElementById('scanApButton');
  const getAccessPointsTbody = () => document.getElementById('accessPointsTbody');

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let apDlg, apForm, apTitle, accessPointsTbody, scanApButton,
      apDistributorDlg, apDistributorForm, apDistributorSelect,
      accessPointSearchInput;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    apDlg = document.getElementById('apDlg');
    apForm = document.getElementById('apForm');
    apTitle = document.getElementById('apDlgTitle');
    // The HTML element has id="accessPointsTbody" (with an 's'); the
    // original admin.js declared a buggy `const apsTbody` from
    // 'accessPointTbody' (no 's') that was never used — code accessed
    // `accessPointsTbody` via the browser's named-element window global.
    // Resolve it explicitly to be safe.
    accessPointsTbody = (getAccessPointsTbody() || document.getElementById('accessPointsTbody'));
    scanApButton = getScanApButton();
    apDistributorDlg = document.getElementById('apDistributorDlg');
    apDistributorForm = document.getElementById('apDistributorForm');
    apDistributorSelect = document.getElementById('apDistributorSelect');
    accessPointSearchInput = document.getElementById('accessPointSearch');
  }

  function renderAps(items = []) {
    accessPointsTbody.innerHTML = '';
    if (!items.length || items.length === 0) {
      accessPointsTbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">No Aps</td></tr>';
      return;
    }
    const distCache = getDistributorCache();
    const distById = distCache && distCache.byId ? distCache.byId : new Map();
    items.forEach((p, idx) => {
      const tr = document.createElement('tr');
      const canWrite = hasRole('root', 'hotspot_admin');
      const dist = p.distributor_id ? distById.get(Number(p.distributor_id)) : null;
      tr.innerHTML = `
        <td>${idx + 1}</td>
        <td>${escapeHtml(p.ap_name)}</td>
        <td>${escapeHtml(p.location || '')}</td>
        <td>${escapeHtml(p.ap_mac || '')}</td>
        <td>${escapeHtml(p.last_seen_ip || '')}</td>
        <td>${escapeHtml(dist ? (dist.name || dist.phone || '') : '')}</td>
        <td>${escapeHtml(p.last_seen ? formatDateTimeFull(p.last_seen) : '')}</td>
        <td>${escapeHtml(p.is_enabled ? 'enabled' : 'disabled')}</td>
        <td>
          ${canWrite ? `<button class="btn warn" data-act="toggle" data-id="${p.ap_name}" data-enabled="${p.is_enabled}">${p.is_enabled ? 'Disable' : 'Enable'}</button>` : ''}
          ${canWrite ? `<button class="btn" data-act="assign_distributor" data-id="${p.ap_name}">🏷 Assign</button>` : ''}
          ${canWrite ? `<button class="btn edit" data-act="edit" data-id="${p.ap_name}">✏️ Edit</button>` : ''}
        </td>`;
      accessPointsTbody.appendChild(tr);
    });
  }

  function scanAps() {
    if (!confirm('Scan for new APs? This may take a while.')) return;
    scanApButton.disabled = true;
    scanApButton.textContent = 'Scanning...';
    adminAPI.fetch(API.aps + 'scan', { method: 'POST' })
      .then(data => {
        showFlash(`Scan complete: ${data.created || 0} new, ${data.updated || 0} updated`);
        loadAPs();
      })
      .catch(err => showFlash(err.message, 'error'))
      .finally(() => {
        scanApButton.disabled = false;
        scanApButton.textContent = 'Scan APs';
      });
  }

  async function openApDialog(ap_name = null) {
    apForm.reset();
    apForm.id.value = ap_name;
    apTitle.textContent = ap_name ? 'Edit AP' : 'Add AP';
    if (ap_name) {
      try {
        const p = await adminAPI.fetch(API.ap(ap_name), { method: 'GET' });
        apForm.connection_account.value = p.connection_account || '';
      } catch (err) { showFlash(err.message, 'error'); return; }
    }
    openDialog(apDlg);
  }

  async function openApDistributorDialog(apName) {
    if (!apDistributorDlg || !apDistributorForm || !apDistributorSelect) { console.warn('access-points: distributor-assign dialog elements missing.'); return; }
    apDistributorForm.reset();
    apDistributorForm.ap_name.value = apName;
    await ensureDistributorCache();
    const distCache = getDistributorCache();
    apDistributorSelect.innerHTML = '<option value="">Unassigned</option>';
    (distCache.items || []).forEach((d) => {
      const opt = document.createElement('option');
      opt.value = String(d.id);
      opt.textContent = `${d.name || ''} (${d.phone || ''})`;
      apDistributorSelect.appendChild(opt);
    });
    try {
      const ap = await adminAPI.fetch(API.ap(apName), { method: 'GET' });
      if (ap && ap.distributor_id) apDistributorSelect.value = String(ap.distributor_id);
    } catch (_) {}
    openDialog(apDistributorDlg);
  }

  async function loadAPs() {
    try {
      await ensureDistributorCache();
      const data = await adminAPI.fetch(API.aps, { method: 'GET' });
      renderAps(Array.isArray(data) ? data : (data.items || []));
    }
    catch (err) { renderAps([]); showFlash(err.message, 'error'); }
  }

  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();
    const addProductBtn = getAddProductBtn();
    if (addProductBtn) addProductBtn.addEventListener('click', () => openApDialog());
    if (accessPointSearchInput) {
      accessPointSearchInput.addEventListener('input', (e) => filterTable('accessPointTable', e.target.value));
    }
    if (scanApButton) {
      scanApButton.addEventListener('click', () => scanAps('access_points'));
    }
    if (accessPointsTbody) {
      accessPointsTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-act]');
        if (!btn) return;
        const id = btn.dataset.id;
        const act = btn.dataset.act;
        if (act === 'edit') return openApDialog(id);
        if (act === 'assign_distributor') return openApDistributorDialog(id);
        if (act === 'toggle') {
          const current = btn.dataset.enabled === 'true';
          if (!confirm(`${current ? 'Disable' : 'Enable'} this ap?`)) return;
          try {
            try {
              await adminAPI.fetch(API.ap(id), { method: 'PATCH', body: JSON.stringify({ is_enabled: !current }) });
            } catch (err) {
              if (err.status === 404) await adminAPI.fetch(API.apDisable(id), { method: 'POST' }); else throw err;
            }
            showFlash('Ap updated');
            loadAPs();
          } catch (err) {
            showFlash(err.message, 'error');
          }
        }
      });
    }
    if (apForm) {
      apForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const id = apForm.id.value.trim();
        const payload = {
          connection_account: apForm.connection_account.value.trim()
        };
        try {
          if (id) await adminAPI.fetch(API.ap(id), { method: 'PUT', body: JSON.stringify(payload) });
          else await adminAPI.fetch(API.aps, { method: 'POST', body: JSON.stringify(payload) });
          apDlg.close();
          showFlash('AP saved');
          loadAPs();
        } catch (err) { showFlash(err.message, 'error'); }
      });
    }
    if (apDistributorForm) {
      apDistributorForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const apName = apDistributorForm.ap_name.value;
        const distId = apDistributorSelect.value ? Number(apDistributorSelect.value) : null;
        try {
          await adminAPI.fetch(API.adminDistributorAssignAp, {
            method: 'POST',
            body: JSON.stringify({ ap_name: apName, distributor_id: distId }),
          });
          apDistributorDlg.close();
          showFlash('AP updated');
          await ensureDistributorCache();
          loadAPs();
        } catch (err) {
          showFlash(err.message, 'error');
        }
      });
    }
  }

export default {
  mount,
  init: () => { resolveDom(); loadAPs(); },
};
